import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import useGameChallenge from '../hooks/useGameChallenge';
import ChallengeInviteModal from '../components/ChallengeInviteModal';
import ChallengeDuelBanner from '../components/ChallengeDuelBanner';
import RivalryCard from '../components/RivalryCard';
import Icon from '../components/Icon';

const SEASONS = ['2022', '2023', '2024', '2025'];
const JOLPICA_BASE = 'https://api.jolpi.ca/ergast/f1';

const SESSION_TIERS = [
  { min: 36, xp: 1500, label: '≥ 36 pts' },
  { min: 24, xp: 1000, label: '≥ 24 pts' },
  { min: 18, xp: 600,  label: '≥ 18 pts' },
  { min: 12, xp: 300,  label: '≥ 12 pts' },
  { min:  6, xp: 150,  label: '≥ 6 pts'  },
  { min:  1, xp:  50,  label: '≥ 1 pt'   },
];

const FLAGS = {
  'Australian Grand Prix': '🇦🇺', 'Chinese Grand Prix': '🇨🇳',
  'Japanese Grand Prix': '🇯🇵', 'Bahrain Grand Prix': '🇧🇭',
  'Saudi Arabian Grand Prix': '🇸🇦', 'Miami Grand Prix': '🇺🇸',
  'Emilia Romagna Grand Prix': '🇮🇹', 'Monaco Grand Prix': '🇲🇨',
  'Canadian Grand Prix': '🇨🇦', 'Spanish Grand Prix': '🇪🇸',
  'Austrian Grand Prix': '🇦🇹', 'British Grand Prix': '🇬🇧',
  'Hungarian Grand Prix': '🇭🇺', 'Belgian Grand Prix': '🇧🇪',
  'Dutch Grand Prix': '🇳🇱', 'Italian Grand Prix': '🇮🇹',
  'Singapore Grand Prix': '🇸🇬', 'Azerbaijan Grand Prix': '🇦🇿',
  'United States Grand Prix': '🇺🇸', 'Mexico City Grand Prix': '🇲🇽',
  'São Paulo Grand Prix': '🇧🇷', 'Las Vegas Grand Prix': '🇺🇸',
  'Qatar Grand Prix': '🇶🇦', 'Abu Dhabi Grand Prix': '🇦🇪',
};

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function driverKey(d) { return d.Driver.driverId; }
function driverName(d) { return `${d.Driver.givenName} ${d.Driver.familyName}`; }

const SLOT_LABELS = ['P1', 'P2', 'P3'];
const SLOT_COLORS = {
  correct: 'border-green-500 bg-green-500/10',
  wrong:   'border-red-500 bg-red-500/10',
  empty:   'border-zinc-700 border-dashed',
  filled:  'border-zinc-500 bg-zinc-800',
  over:    'border-f1red bg-f1red/10',
};

const TIMER_SECONDS = 30;

const PodiumQuiz = () => {
  const { user, refreshUser } = useAuth();

  const [races, setRaces]             = useState([]);
  const [raceQueue, setRaceQueue]     = useState([]);
  const [queueIndex, setQueueIndex]   = useState(0);
  const [race, setRace]               = useState(null);
  const [candidates, setCandidates]   = useState([]);
  const [slots, setSlots]             = useState([null, null, null]);
  const [phase, setPhase]             = useState('idle'); // idle | playing | roundover | gameover
  const [selected, setSelected]       = useState(null);
  const [dragOver, setDragOver]       = useState(null);
  const [totalScore, setTotalScore]   = useState(0);
  const [roundsWon, setRoundsWon]     = useState(0);
  const [lastRoundScore, setLastRoundScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myBest, setMyBest]           = useState(null);
  const [timeLeft, setTimeLeft]       = useState(TIMER_SECONDS);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [showChallengeModal, setShowChallengeModal] = useState(false);

  const { activeChallenge, opponent, myScore, opponentScore, hasSubmitted, isCompleted, iWon, submitScore, submitting: cSubmitting, refresh: refreshChallenge } = useGameChallenge('podiumquiz');

  const dragRef   = useRef(null);
  const timerRef  = useRef(null);

  const fetchLeaderboard = useCallback(() => {
    api.get('/game/podium/leaderboard').then(r => setLeaderboard(r.data)).catch(() => {});
    if (user) api.get('/game/podium/best').then(r => setMyBest(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    const load = async () => {
      try {
        const fetched = await Promise.all(
          SEASONS.map(s =>
            fetch(`${JOLPICA_BASE}/${s}/results.json?limit=100`)
              .then(r => r.json())
              .then(d => (d?.MRData?.RaceTable?.Races ?? []).map(r => ({ ...r, season: s })))
              .catch(() => [])
          )
        );
        const all = fetched.flat().filter(r => r.Results && r.Results.length >= 3);
        setRaces(all);
        if (all.length === 0) setError('Aucune course disponible pour le moment.');
      } catch {
        setError('Impossible de charger les courses.');
      } finally {
        setLoading(false);
      }
    };
    load();
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const loadRaceFromQueue = useCallback((queue, idx) => {
    if (!queue.length) return;
    const safeIdx = idx % queue.length;
    const chosen = queue[safeIdx];
    setRace(chosen);
    setQueueIndex(safeIdx + 1);
    const podium = chosen.Results.slice(0, 3);
    const decoys = shuffle(chosen.Results.slice(3, 10)).slice(0, 3);
    setCandidates(shuffle([...podium, ...decoys]));
    setSlots([null, null, null]);
    setSelected(null);
    setTimeLeft(TIMER_SECONDS);
    setPhase('playing');
  }, []);

  const startGame = useCallback(() => {
    if (!races.length) return;
    setTotalScore(0);
    setRoundsWon(0);
    setLastRoundScore(0);
    const q = shuffle(races);
    setRaceQueue(q);
    loadRaceFromQueue(q, 0);
  }, [races, loadRaceFromQueue]);

  const nextRound = useCallback(() => {
    // If queue exhausted, reshuffle
    const nextIdx = queueIndex >= raceQueue.length ? 0 : queueIndex;
    const q = nextIdx === 0 ? shuffle(races) : raceQueue;
    if (nextIdx === 0) setRaceQueue(q);
    loadRaceFromQueue(q, nextIdx);
  }, [queueIndex, raceQueue, races, loadRaceFromQueue]);

  const roundScore = useCallback(() => {
    if (!race) return 0;
    const podium = race.Results.slice(0, 3);
    return slots.reduce((acc, s, i) => {
      if (!s) return acc;
      return acc + (driverKey(s) === driverKey(podium[i]) ? (3 - i) : 0);
    }, 0);
  }, [race, slots]);

  const validateForced = useCallback(async () => {
    const rs = slots.reduce((acc, s, i) => {
      if (!s || !race) return acc;
      return acc + (driverKey(s) === driverKey(race.Results[i]) ? (3 - i) : 0);
    }, 0);
    setLastRoundScore(rs);
    if (rs === 0) {
      setPhase('gameover');
      if (user && totalScore > 0) {
        try {
          const tier = SESSION_TIERS.find(t => totalScore >= t.min);
          await api.post('/game/podium', { score: totalScore, xpEarned: tier?.xp ?? 0 });
          refreshUser();
        } catch {}
      }
      fetchLeaderboard();
    } else {
      setTotalScore(prev => prev + rs);
      setRoundsWon(prev => prev + 1);
      setPhase('roundover');
    }
  }, [slots, race, totalScore, user, refreshUser, fetchLeaderboard]);

  // Timer effect
  useEffect(() => {
    if (phase !== 'playing') { clearInterval(timerRef.current); return; }
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, race]);

  // Auto-validate when timer hits 0
  useEffect(() => {
    if (timeLeft === 0 && phase === 'playing') {
      validateForced();
    }
  }, [timeLeft, phase, validateForced]);

  const validate = useCallback(async () => {
    if (slots.some(s => s === null)) return;
    const rs = roundScore();
    setLastRoundScore(rs);
    if (rs === 0) {
      setPhase('gameover');
      if (user && totalScore > 0) {
        try {
          const tier = SESSION_TIERS.find(t => totalScore >= t.min);
          await api.post('/game/podium', { score: totalScore, xpEarned: tier?.xp ?? 0 });
          refreshUser();
        } catch {}
      }
      fetchLeaderboard();
    } else {
      setTotalScore(prev => prev + rs);
      setRoundsWon(prev => prev + 1);
      setPhase('roundover');
    }
  }, [slots, roundScore, totalScore, user, refreshUser, fetchLeaderboard]);

  // ── Drag & Drop ──
  const onDragStart = (driver) => { dragRef.current = driver; };
  const onDragOver  = (e, i)   => { e.preventDefault(); setDragOver(i); };
  const onDragLeave = ()        => setDragOver(null);
  const onDrop = (e, slotIdx) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragRef.current || phase !== 'playing') return;
    const driver = dragRef.current;
    setSlots(prev => {
      const next = [...prev];
      // If driver is already in another slot, clear it
      next.forEach((s, i) => { if (s && driverKey(s) === driverKey(driver)) next[i] = null; });
      next[slotIdx] = driver;
      return next;
    });
    setSelected(null);
    dragRef.current = null;
  };

  // ── Click select + place ──
  const onCardClick = (driver) => {
    if (phase !== 'playing') return;
    if (selected && driverKey(selected) === driverKey(driver)) {
      setSelected(null);
    } else {
      setSelected(driver);
    }
  };

  const onSlotClick = (slotIdx) => {
    if (phase !== 'playing') return;
    if (selected) {
      setSlots(prev => {
        const next = [...prev];
        next.forEach((s, i) => { if (s && driverKey(s) === driverKey(selected)) next[i] = null; });
        next[slotIdx] = selected;
        return next;
      });
      setSelected(null);
    } else if (slots[slotIdx]) {
      setSelected(slots[slotIdx]);
      setSlots(prev => { const n = [...prev]; n[slotIdx] = null; return n; });
    }
  };

  const removeFromSlot = (slotIdx) => {
    if (phase !== 'playing') return;
    setSlots(prev => { const n = [...prev]; n[slotIdx] = null; return n; });
  };

  // ── Helpers ──
  const isInSlot   = (driver) => slots.some(s => s && driverKey(s) === driverKey(driver));
  const isSelected = (driver) => selected && driverKey(selected) === driverKey(driver);
  const isCorrect  = (slotIdx) => {
    if (!race || !slots[slotIdx]) return null;
    return driverKey(slots[slotIdx]) === driverKey(race.Results[slotIdx]);
  };
  const allFilled    = slots.every(s => s !== null);
  const isRevealed   = phase === 'roundover' || phase === 'gameover';
  const sessionTier  = SESSION_TIERS.find(t => totalScore >= t.min) || null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="mb-6">
        <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium mb-1">Box-Box · PodiumQuiz</p>
        <h1 className="text-3xl font-black text-white">PodiumQuiz</h1>
        <p className="text-zinc-400 text-sm mt-1">Reconstitue le podium en glissant les pilotes — courses 2022 à 2025.</p>
      </div>

      <ChallengeDuelBanner
        activeChallenge={activeChallenge}
        opponent={opponent}
        myScore={myScore}
        opponentScore={opponentScore}
        hasSubmitted={hasSubmitted}
        isCompleted={isCompleted}
        iWon={iWon}
        myLiveScore={phase === 'gameover' || phase === 'playing' || phase === 'roundover' ? totalScore : null}
        canSubmit={phase === 'gameover' && !hasSubmitted}
        formatScore={(s) => s !== null ? `${s} pts` : null}
        onSubmit={() => submitScore(totalScore)}
        submitting={cSubmitting}
        game="podiumquiz"
      />
      <RivalryCard opponent={opponent} game="podiumquiz" />

      {loading && <p className="text-zinc-500 text-sm text-center py-12">Chargement des courses 2025…</p>}
      {error   && <p className="text-red-400 text-sm text-center py-12">{error}</p>}

      {/* ── IDLE ── */}
      {!loading && !error && phase === 'idle' && (
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-white font-black text-lg mb-4">Comment jouer ?</h2>
            <ol className="space-y-3 text-zinc-400 text-sm">
              <li className="flex gap-3"><span className="text-f1red font-black shrink-0">1.</span>Une course (2022-2025) s'affiche. <strong className="text-white">Glisse</strong> (ou clique) les pilotes en P1, P2, P3.</li>
              <li className="flex gap-3"><span className="text-f1red font-black shrink-0">2.</span>Score par course : <strong className="text-white">P1 = 3 pts · P2 = 2 pts · P3 = 1 pt.</strong></li>
              <li className="flex gap-3"><span className="text-f1red font-black shrink-0">3.</span>Les points s'accumulent de course en course.</li>
              <li className="flex gap-3"><span className="text-f1red font-black shrink-0">4.</span><strong className="text-red-400">⏱ 30 secondes par course.</strong> Le timer expire ? Le podium se valide automatiquement.</li>
              <li className="flex gap-3"><span className="text-f1red font-black shrink-0">5.</span><strong className="text-red-400">0/6 sur une course = GAME OVER.</strong> Ton total est sauvegardé.</li>
            </ol>
            <div className="mt-4 flex flex-wrap gap-2">
              {SESSION_TIERS.map(t => (
                <span key={t.min} className="text-xs font-bold px-2.5 py-1 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">
                  {t.label} → {t.xp} XP
                </span>
              ))}
            </div>
            <button onClick={startGame} className="btn-primary w-full mt-6 py-3 text-base">
              Lancer la session
            </button>
            {user && (
              <button onClick={() => setShowChallengeModal(true)}
                className="w-full mt-2 py-2.5 rounded-xl text-sm font-bold border border-zinc-700 text-zinc-300 hover:border-f1red/50 hover:text-white transition-all">
                <Icon name="sword" size={13} className="shrink-0" /> Affronter un ami
              </button>
            )}
          </div>
          <LeaderboardSection leaderboard={leaderboard} myBest={myBest} user={user} />
        </div>
      )}

      {/* ── PLAYING / ROUNDOVER / GAMEOVER ── */}
      {(phase === 'playing' || phase === 'roundover' || phase === 'gameover') && race && (
        <div className="space-y-5">
          {/* Race header + live score + timer */}
          <div className="card py-4 flex items-center gap-3">
            <span className="text-3xl">{FLAGS[race.raceName] ?? <Icon name="flag" size={28} />}</span>
            <div className="flex-1 min-w-0">
              <p className="text-white font-black text-lg leading-tight truncate">{race.raceName}</p>
              <p className="text-zinc-500 text-xs">{race.season} · Manche {race.round}</p>
            </div>
            {/* Timer */}
            {phase === 'playing' && (
              <div className="shrink-0 flex flex-col items-center gap-0.5">
                <div className={`text-2xl font-black tabular-nums ${timeLeft <= 10 ? 'text-red-400' : timeLeft <= 20 ? 'text-yellow-400' : 'text-white'}`}>
                  {timeLeft}s
                </div>
                <div className="w-12 h-1 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000"
                    style={{
                      width: `${(timeLeft / TIMER_SECONDS) * 100}%`,
                      background: timeLeft <= 10 ? '#ef4444' : timeLeft <= 20 ? '#facc15' : '#22c55e',
                    }} />
                </div>
              </div>
            )}
            {(roundsWon > 0 || phase !== 'playing') && (
              <div className="text-right shrink-0">
                <p className="text-white font-black text-xl">{totalScore} <span className="text-zinc-500 text-sm font-normal">pts</span></p>
                <p className="text-zinc-600 text-xs">{roundsWon} course{roundsWon > 1 ? 's' : ''} réussie{roundsWon > 1 ? 's' : ''}</p>
              </div>
            )}
          </div>

          {/* Podium slots */}
          <div className="grid grid-cols-3 gap-3">
            {SLOT_LABELS.map((label, i) => {
              const driver = slots[i];
              const correct = isRevealed ? isCorrect(i) : null;
              const over = dragOver === i;
              let cls = 'border-2 rounded-2xl min-h-[90px] flex flex-col items-center justify-center p-3 transition-all cursor-pointer ';
              if (isRevealed) {
                cls += correct ? SLOT_COLORS.correct : SLOT_COLORS.wrong;
              } else if (over) {
                cls += SLOT_COLORS.over;
              } else if (driver) {
                cls += SLOT_COLORS.filled;
              } else {
                cls += SLOT_COLORS.empty;
              }
              return (
                <div key={label} className={cls}
                  onDragOver={e => onDragOver(e, i)}
                  onDragLeave={onDragLeave}
                  onDrop={e => onDrop(e, i)}
                  onClick={() => onSlotClick(i)}
                >
                  <span className={`text-xs font-black mb-1 ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-zinc-300' : 'text-orange-400'}`}>{label}</span>
                  {driver ? (
                    <div className="text-center">
                      <p className="text-white text-xs font-bold leading-tight">{driverName(driver)}</p>
                      <p className="text-zinc-500 text-[10px]">{driver.Constructor.name}</p>
                      {isRevealed && (
                        <span className={`text-xs font-black mt-1 block ${correct ? 'text-green-400' : 'text-red-400'}`}>
                          {correct ? `+${3 - i} pt${3-i>1?'s':''}` : '✗'}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-zinc-600 text-xs">{selected ? 'Déposer ici' : 'Vide'}</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Correct answers reveal */}
          {isRevealed && (
            <div className={`card ${phase === 'gameover' ? 'border-red-500/30 bg-red-500/5' : 'bg-zinc-900'}`}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-zinc-400 text-xs uppercase tracking-widest font-bold">Podium réel</p>
                {phase === 'gameover'
                  ? <span className="text-red-400 text-xs font-black">0/6 — GAME OVER</span>
                  : <span className="text-green-400 text-xs font-black">+{lastRoundScore} pt{lastRoundScore > 1 ? 's' : ''} ce tour</span>
                }
              </div>
              <div className="flex flex-col gap-2">
                {race.Results.slice(0, 3).map((r, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className={`text-sm font-black w-6 text-center ${i===0?'text-yellow-400':i===1?'text-zinc-300':'text-orange-400'}`}>P{i+1}</span>
                    <span className="text-white text-sm font-semibold">{driverName(r)}</span>
                    <span className="text-zinc-500 text-xs">{r.Constructor.name}</span>
                    {slots[i] && driverKey(slots[i]) === driverKey(r) && <span className="text-green-400 text-xs font-black ml-auto">✓</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Candidates pool */}
          {(phase === 'playing') && (
            <div>
              <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2 font-medium">Pilotes disponibles</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {candidates.map(driver => {
                  const inSlot = isInSlot(driver);
                  const isSel  = isSelected(driver);
                  if (inSlot) return null;
                  return (
                    <div key={driverKey(driver)}
                      draggable
                      onDragStart={() => onDragStart(driver)}
                      onDragEnd={() => { dragRef.current = null; setDragOver(null); }}
                      onClick={() => onCardClick(driver)}
                      className={`px-3 py-2.5 rounded-xl border text-sm font-semibold cursor-grab active:cursor-grabbing select-none transition-all
                        ${isSel
                          ? 'border-f1red bg-f1red/10 text-white scale-[1.02]'
                          : 'border-zinc-700 hover:border-zinc-500 text-zinc-200 hover:bg-zinc-800/50'
                        }`}>
                      <p className="font-bold text-sm leading-tight">{driverName(driver)}</p>
                      <p className="text-zinc-500 text-[10px]">{driver.Constructor.name}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Gameover summary */}
          {phase === 'gameover' && (
            <div className="card text-center py-8">
              <p className="text-zinc-500 text-xs uppercase tracking-widest mb-3">Session terminée</p>
              <p className="text-7xl font-black text-white mb-1">{totalScore}</p>
              <p className="text-zinc-400 text-sm mb-3">points au total · {roundsWon} course{roundsWon !== 1 ? 's' : ''} réussie{roundsWon !== 1 ? 's' : ''}</p>
              {sessionTier ? (
                <p className="text-green-400 font-bold text-lg">+{sessionTier.xp} XP ✓</p>
              ) : (
                <p className="text-zinc-500 text-sm">Aucun XP — il faut au moins 1 pt.</p>
              )}
              {!user && <p className="text-zinc-500 text-xs mt-2">Connecte-toi pour sauvegarder.</p>}
            </div>
          )}

          {/* Roundover mini summary */}
          {phase === 'roundover' && (
            <div className="card flex items-center justify-between py-4">
              <div>
                <p className="text-green-400 font-black text-sm">+{lastRoundScore} pt{lastRoundScore > 1 ? 's' : ''} ce tour</p>
                <p className="text-zinc-500 text-xs">Total : <span className="text-white font-bold">{totalScore} pts</span></p>
              </div>
              <span className="text-2xl">✅</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            {phase === 'playing' && (
              <button onClick={validate} disabled={!allFilled}
                className="btn-primary flex-1 py-3 disabled:opacity-40 disabled:cursor-not-allowed">
                Valider le podium ✓
              </button>
            )}
            {phase === 'roundover' && (
              <button onClick={nextRound} className="btn-primary flex-1 py-3">
                Course suivante →
              </button>
            )}
            {phase === 'gameover' && (
              <>
                <button onClick={startGame} className="btn-primary flex-1 py-3">Rejouer</button>
                <button onClick={() => setPhase('idle')} className="flex-1 py-3 rounded-xl border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-sm font-semibold transition-colors">
                  Classement
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {phase !== 'idle' && (
        <div className="mt-8">
          <LeaderboardSection leaderboard={leaderboard} myBest={myBest} user={user} />
        </div>
      )}
      {showChallengeModal && (
        <ChallengeInviteModal game="podiumquiz"
          onClose={() => setShowChallengeModal(false)}
          onSent={() => { setShowChallengeModal(false); refreshChallenge(); }} />
      )}
    </div>
  );
};

const LeaderboardSection = ({ leaderboard, myBest, user }) => (
  <div className="grid md:grid-cols-2 gap-6">
    <div className="card">
      <h3 className="text-white font-black mb-4 flex items-center gap-2"><Icon name="trophy" size={14} /> Meilleurs scores</h3>
      {leaderboard.length === 0 ? (
        <p className="text-zinc-500 text-sm text-center py-6">Aucun score pour l'instant.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {leaderboard.slice(0, 10).map((e, i) => (
            <div key={e._id || i} className={`flex items-center gap-3 px-3 py-2 rounded-xl ${i===0?'bg-yellow-500/10 border border-yellow-500/20':'bg-zinc-800/40'}`}>
              <span className={`text-sm font-black w-5 text-center ${i===0?'text-yellow-400':i===1?'text-zinc-300':i===2?'text-orange-400':'text-zinc-600'}`}>{i+1}</span>
              <span className="flex-1 text-white text-sm font-semibold truncate">{e.username}</span>
              <span className="text-white font-black text-sm">{e.best}<span className="text-zinc-500 text-xs font-normal"> pts</span></span>
            </div>
          ))}
        </div>
      )}
    </div>
    <div className="card">
      <h3 className="text-white font-black mb-4">📊 Mon meilleur</h3>
      {!user ? (
        <p className="text-zinc-500 text-sm text-center py-6">Connecte-toi pour suivre tes scores.</p>
      ) : !myBest || myBest.best === null ? (
        <p className="text-zinc-500 text-sm text-center py-6">Aucune partie jouée.</p>
      ) : (
        <div className="text-center py-4">
          <p className="text-5xl font-black text-white mb-1">{myBest.best}<span className="text-zinc-500 text-xl"> pts</span></p>
          <p className="text-zinc-500 text-xs mt-2">{myBest.plays} partie{myBest.plays !== 1 ? 's' : ''} jouée{myBest.plays !== 1 ? 's' : ''}</p>
        </div>
      )}
    </div>
  </div>
);

export default PodiumQuiz;
